import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../../context/LanguageContext";
import { CONTACT } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  const field =
    "w-full bg-[#020617] border border-white/15 rounded-lg px-4 py-3.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] transition-all duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 md:py-36 border-t border-white/10 bg-[#0F172A]/40">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <p className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#60A5FA] mb-6" data-testid="contact-overline">
            {t.contact.overline}
          </p>
          <h2 className="font-display font-bold tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-6xl max-w-3xl" data-testid="contact-title">
            {t.contact.title}
          </h2>
          <p className="mt-6 max-w-xl text-slate-400 text-base leading-relaxed">{t.contact.body}</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-2 rounded-2xl border border-white/10 bg-[#020617] p-8"
            data-testid="contact-info-card"
          >
            <h3 className="font-mono-tech text-xs tracking-[0.25em] uppercase text-slate-500 mb-8">
              {t.contact.infoTitle}
            </h3>
            <div className="space-y-7">
              <div className="flex items-start gap-4">
                <img
                  src="/owner.jpg"
                  alt={t.contact.owner}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#2563EB] shrink-0"
                />
                <div>
                  <div className="text-white font-medium" data-testid="contact-owner">{t.contact.owner}</div>
                  <div className="text-slate-400 text-sm">{t.contact.role}</div>
                </div>
              </div>
              <a href={`tel:${CONTACT.phoneHref}`} data-testid="contact-phone-link" className="flex items-start gap-4 group">
                <Phone size={20} className="text-[#2563EB] mt-0.5 shrink-0" />
                <span className="text-slate-200 group-hover:text-[#60A5FA] transition-colors">{CONTACT.phone}</span>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-start gap-4 group">
                <Mail size={20} className="text-[#2563EB] mt-0.5 shrink-0" />
                <span className="text-slate-200 group-hover:text-[#60A5FA] transition-colors break-all">{CONTACT.email}</span>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[#2563EB] mt-0.5 shrink-0" />
                <span className="text-slate-200" data-testid="contact-address">{CONTACT.address}</span>
              </div>
            </div>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-3 rounded-2xl border border-white/10 bg-[#020617] p-8 space-y-5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.form.name}
                className={field}
              />
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.form.email}
                className={field}
              />
            </div>
            <input
              data-testid="contact-company-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder={t.contact.form.company}
              className={field}
            />
            <textarea
              data-testid="contact-message-input"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.contact.form.message}
              className={`${field} resize-none`}
            />
            <button
              data-testid="contact-submit-btn"
              type="submit"
              disabled={sending}
              className="w-full md:w-auto px-8 py-3.5 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-60 text-white font-medium transition-all duration-300 hover:scale-[1.03]"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
